/**
 * Unified filter utilities for all data types
 * Provides generic and specialized filtering functions
 */

import type { ProcessedSet } from "@workspace/types";
import { parseGenres } from "@workspace/types";

/**
 * Normalize text for search comparisons
 * Removes accents, converts to lowercase, and trims whitespace
 */
export function normalizeForSearch(text: string): string {
	return text
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase()
		.trim();
}

/**
 * Check if text matches search term (normalized)
 */
export function matchesSearch(text: string, searchTerm: string): boolean {
	return normalizeForSearch(text).includes(normalizeForSearch(searchTerm));
}

/**
 * Apply genre filter to an array of items with genre data.
 * Handles both "no-genre" and specific genre filtering with consistent logic.
 *
 * @param items - Array of items to filter
 * @param selectedGenre - The genre to filter by ("no-genre" or specific genre)
 * @param getGenres - Function to extract genre data from an item
 * @returns Filtered array of items
 */
export function applyGenreFilter<T>(
	items: T[],
	selectedGenre: string | null,
	getGenres: (item: T) => {
		spotifyGenres?: string[] | string | null;
		manualGenres?: string[] | string | null;
	},
): T[] {
	if (!selectedGenre || selectedGenre === "all") return items;

	return items.filter((item) => {
		const genreData = getGenres(item);

		if (selectedGenre === "no-genre") {
			// Check for no genres - consistent with database logic
			const hasSpotifyGenres =
				genreData.spotifyGenres &&
				(Array.isArray(genreData.spotifyGenres)
					? genreData.spotifyGenres.length > 0
					: genreData.spotifyGenres.trim().length > 0);
			const hasManualGenres =
				genreData.manualGenres &&
				(Array.isArray(genreData.manualGenres)
					? genreData.manualGenres.length > 0
					: genreData.manualGenres.trim().length > 0);

			return !hasSpotifyGenres && !hasManualGenres;
		}

		// For specific genres, use parseGenres for consistency
		const parsedGenres = parseGenres(
			genreData.spotifyGenres || null,
			genreData.manualGenres || null,
		);
		return parsedGenres.includes(selectedGenre);
	});
}

/**
 * Apply genre filter specifically for ProcessedSet arrays (B2B support).
 * Checks if ANY collaborator has the selected genre.
 */
export function applyGenreFilterToSets(
	sets: ProcessedSet[],
	selectedGenre: string | null,
): ProcessedSet[] {
	if (!selectedGenre || selectedGenre === "all") return sets;

	return sets.filter((set) => {
		if (selectedGenre === "no-genre") {
			// For no-genre, check if ALL artists have no genres
			const allArtists = [
				set.artist,
				...(set.set_artists?.map((sa) => sa.artist) || []),
			].filter(Boolean);

			return allArtists.every((artist) => {
				if (!artist) return true;
				const hasSpotifyGenres =
					artist.spotify_genres && artist.spotify_genres.length > 0;
				const hasManualGenres =
					artist.manual_genres && artist.manual_genres.length > 0;
				return !hasSpotifyGenres && !hasManualGenres;
			});
		}

		// For specific genres, check if ANY artist has the genre
		const allArtists = [
			set.artist,
			...(set.set_artists?.map((sa) => sa.artist) || []),
		].filter(Boolean);

		return allArtists.some((artist) => {
			if (!artist) return false;
			const parsedGenres = parseGenres(
				artist.spotify_genres || null,
				artist.manual_genres || null,
			);
			// Normalize to lowercase for comparison (genres are stored in various cases)
			return parsedGenres.some(g => g.toLowerCase() === selectedGenre.toLowerCase());
		});
	});
}

/**
 * Apply search filter to an array of items.
 * Uses normalized, accent-insensitive search with configurable fields.
 *
 * @param items - Array of items to filter
 * @param searchTerm - The search term to filter by
 * @param getSearchableFields - Function to extract searchable text from an item
 * @returns Filtered array of items
 */
export function applySearchFilter<T>(
	items: T[],
	searchTerm: string,
	getSearchableFields: (item: T) => string[],
): T[] {
	if (!searchTerm || searchTerm.trim().length < 2) return items;

	const normalizedSearch = normalizeForSearch(searchTerm);

	return items.filter((item) => {
		const searchableFields = getSearchableFields(item);
		return searchableFields.some(
			(field) => field && matchesSearch(field, normalizedSearch),
		);
	});
}

/**
 * Sort an array of items by a specific field with configurable direction.
 * Handles different data types (string, number, date) with proper comparison.
 *
 * @param items - Array of items to sort
 * @param sortBy - Sort configuration (field and direction)
 * @param getValue - Function to extract the sortable value from an item
 * @returns Sorted array of items
 */
export function applySortOrder<T>(
	items: T[],
	sortBy: { field: string; direction: "asc" | "desc" },
	getValue: (item: T, field: string) => unknown,
): T[] {
	const sortedItems = [...items];

	sortedItems.sort((a, b) => {
		const aValue = getValue(a, sortBy.field);
		const bValue = getValue(b, sortBy.field);

		// Handle null/undefined values
		if (aValue == null && bValue == null) return 0;
		if (aValue == null) return sortBy.direction === "asc" ? 1 : -1;
		if (bValue == null) return sortBy.direction === "asc" ? -1 : 1;

		// Handle different types
		if (typeof aValue === "string" && typeof bValue === "string") {
			// Use locale-aware string comparison
			const result = aValue.localeCompare(bValue, "en-US", {
				sensitivity: "base",
				numeric: true,
			});
			return sortBy.direction === "asc" ? result : -result;
		}

		if (typeof aValue === "number" && typeof bValue === "number") {
			return sortBy.direction === "asc" ? aValue - bValue : bValue - aValue;
		}

		// Handle dates
		if (aValue instanceof Date && bValue instanceof Date) {
			const result = aValue.getTime() - bValue.getTime();
			return sortBy.direction === "asc" ? result : -result;
		}

		// Fallback to string comparison
		const result = String(aValue).localeCompare(String(bValue));
		return sortBy.direction === "asc" ? result : -result;
	});

	return sortedItems;
}

/**
 * Multi-criteria sort utility for complex sorting scenarios.
 * Applies multiple sort criteria in order of priority.
 *
 * @param items - Array of items to sort
 * @param sortCriteria - Array of sort criteria in priority order
 * @returns Sorted array of items
 */
export function applyMultiSort<T>(
	items: T[],
	sortCriteria: Array<{
		field: string;
		direction: "asc" | "desc";
		getValue: (item: T) => unknown;
	}>,
): T[] {
	const sortedItems = [...items];

	sortedItems.sort((a, b) => {
		for (const criterion of sortCriteria) {
			const aValue = criterion.getValue(a);
			const bValue = criterion.getValue(b);

			// Skip if values are equal
			if (aValue === bValue) continue;

			// Handle null/undefined
			if (aValue == null && bValue == null) continue;
			if (aValue == null) return criterion.direction === "asc" ? 1 : -1;
			if (bValue == null) return criterion.direction === "asc" ? -1 : 1;

			// Compare values
			let result: number;
			if (typeof aValue === "string" && typeof bValue === "string") {
				result = aValue.localeCompare(bValue, "en-US", {
					sensitivity: "base",
					numeric: true,
				});
			} else if (typeof aValue === "number" && typeof bValue === "number") {
				result = aValue - bValue;
			} else if (aValue instanceof Date && bValue instanceof Date) {
				result = aValue.getTime() - bValue.getTime();
			} else {
				result = String(aValue).localeCompare(String(bValue));
			}

			// Apply direction and return if not equal
			if (result !== 0) {
				return criterion.direction === "asc" ? result : -result;
			}
		}

		return 0;
	});

	return sortedItems;
}

/**
 * Apply date range filter to an array of items.
 * Filters items that fall within the specified date range (inclusive).
 *
 * @param items - Array of items to filter
 * @param startDate - Start of the date range (inclusive)
 * @param endDate - End of the date range (inclusive)
 * @param getDate - Function to extract the date from an item
 * @returns Filtered array of items
 */
export function applyDateRangeFilter<T>(
	items: T[],
	startDate: Date | null,
	endDate: Date | null,
	getDate: (item: T) => Date | string | null,
): T[] {
	if (!startDate && !endDate) return items;

	return items.filter((item) => {
		const itemDate = getDate(item);
		if (!itemDate) return false;

		const date = typeof itemDate === "string" ? new Date(itemDate) : itemDate;

		if (startDate && date < startDate) return false;
		if (endDate && date > endDate) return false;

		return true;
	});
}

/**
 * Apply time range filter for festival sets.
 * Handles overnight festivals with proper time comparison.
 *
 * @param items - Array of items to filter
 * @param startTime - Start time in "HH:MM" format
 * @param endTime - End time in "HH:MM" format
 * @param getTime - Function to extract the time from an item
 * @param isOvernightFestival - Whether the festival runs overnight
 * @returns Filtered array of items
 */
export function applyTimeRangeFilter<T>(
	items: T[],
	startTime: string | null,
	endTime: string | null,
	getTime: (item: T) => string | null,
	isOvernightFestival = false,
): T[] {
	if (!startTime && !endTime) return items;

	return items.filter((item) => {
		const itemTime = getTime(item);
		if (!itemTime) return false;

		const itemHour = Number.parseInt(itemTime.split(":")[0]!);
		const startHour = startTime
			? Number.parseInt(startTime.split(":")[0]!)
			: null;
		const endHour = endTime ? Number.parseInt(endTime.split(":")[0]!) : null;

		if (isOvernightFestival) {
			// For overnight festivals, adjust hours for comparison
			const adjustedItemHour = itemHour < 12 ? itemHour + 24 : itemHour;
			const adjustedStartHour =
				startHour && startHour < 12 ? startHour + 24 : startHour;
			const adjustedEndHour = endHour && endHour < 12 ? endHour + 24 : endHour;

			if (adjustedStartHour && adjustedItemHour < adjustedStartHour)
				return false;
			if (adjustedEndHour && adjustedItemHour > adjustedEndHour) return false;
		} else {
			if (startHour && itemHour < startHour) return false;
			if (endHour && itemHour > endHour) return false;
		}

		return true;
	});
}

/**
 * Create a SQL WHERE clause for genre filtering.
 * Used for server-side filtering in database queries.
 */
export function getGenreFilterSQL(selectedGenre: string): {
	condition: string;
	values: string[];
} {
	if (selectedGenre === "no-genre") {
		return {
			condition: `(spotify_genres IS NULL OR spotify_genres = '{}') AND (manual_genres IS NULL OR manual_genres = '{}')`,
			values: [],
		};
	}

	// For specific genres, check multiple case variations
	const genreVariations = [
		selectedGenre,
		selectedGenre.toLowerCase(),
		selectedGenre.replace(/-/g, " "),
		selectedGenre.replace(/-/g, " ").toLowerCase(),
	];

	return {
		condition: "(spotify_genres && $1 OR manual_genres && $1)",
		values: genreVariations,
	};
}